import isEqual from 'lodash/isEqual'

import * as Sentry from '@sentry/react'

import AppStore from '~publish/legacy/composer/composer/stores/AppStore'
import FluxComposerStore from '~publish/legacy/composer/composer/stores/ComposerStore'

import type { ComposerStore } from '../composerStore'
import { projectFluxOmniContent } from './projectFluxOmniContent'

/** Fields compared for the omni draft, in canonical order for stable reporting. */
export const OMNI_CONTENT_FIELDS = [
  'text',
  'urls',
  'unshortenedUrls',
  'assets',
  'aiAssisted',
] as const

// Coarse buckets for `sessionAgeMs`, so the Sentry issue's tag-distribution
// view shows how fresh a session was at drift time without opening every
// event — e.g. mostly-fresh sessions would point at a seed-time race, mostly
// long-lived ones at something that accumulates or never settles.
const bucketSessionAge = (ms: number): string => {
  if (ms < 1_000) return '<1s'
  if (ms < 10_000) return '<10s'
  if (ms < 60_000) return '<1min'
  if (ms < 600_000) return '<10min'
  return '10min+'
}

/**
 * Parity check run after mirror writes settle.
 *
 * Outside production every invocation compares both sides and reports drift
 * via `console.error` — deterministically, never sampled. That makes drift
 * loud during local development AND turns the whole jest suite into a drift
 * dragnet: `jest-fail-on-console` is active, so any test that drives the
 * composer through a mirror-missed path fails on the spot. (Sampling here
 * would instead produce 1%-flaky test failures — the worst of both worlds.)
 *
 * In production a 1% sample avoids observer overhead on hot paths while still
 * surfacing systematic divergence. Drift is reported as a first-class Sentry
 * event: breadcrumbs only reach Sentry attached to a later error, so a
 * breadcrumb-only signal would make drift that never coincides with an error
 * (a save that succeeds with wrong content) invisible and un-alertable. A
 * breadcrumb is still added as session context for whatever error follows.
 *
 * Only field names and text lengths are logged or sent — never raw content.
 *
 * `sessionAgeMs` — milliseconds since this mirror instance installed — and the
 * current `composerMode` (`create`/`edit`/`duplicate`, or `unknown` before
 * options load) are tagged so a captured event places itself in the
 * composer's lifecycle without breadcrumb archaeology.
 */
export function checkOmniParityAfterMirror(
  store: ComposerStore,
  direction: 'fluxToZustand' | 'zustandToFlux',
  sessionAgeMs = 0,
): void {
  const isProduction = process.env.NODE_ENV === 'production'
  // 0.04 local sample x 0.25 Sentry client sampleRate (PublishSentryClient
  // applies it to every captured event) = ~1% of drift occurrences reported.
  // Keep in sync with the client rate.
  if (isProduction && Math.random() > 0.04) return

  const fluxOmni = FluxComposerStore.getDraft('omni')
  if (!fluxOmni) return

  const flux = projectFluxOmniContent(fluxOmni)
  const zustand = store.getState().omni

  const driftedFields: string[] = []
  if (!isEqual(flux.text, zustand.text)) driftedFields.push('text')
  if (!isEqual(flux.urls, zustand.urls)) driftedFields.push('urls')
  if (!isEqual(flux.unshortenedUrls, zustand.unshortenedUrls))
    driftedFields.push('unshortenedUrls')
  if (!isEqual(flux.assets, zustand.assets)) driftedFields.push('assets')
  if (!isEqual(flux.aiAssisted, zustand.aiAssisted))
    driftedFields.push('aiAssisted')

  if (driftedFields.length === 0) return

  if (!isProduction) {
    // eslint-disable-next-line no-console
    console.error(
      '[composer.bridge] omni mirror drift detected',
      `direction=${direction}`,
      `fields=${driftedFields.join(',')}`,
      `textLength(flux)=${flux.text.length}`,
      `textLength(zustand)=${zustand.text.length}`,
    )
    return
  }

  const composerMode = AppStore.getOptions().composerMode ?? 'unknown'

  Sentry.captureEvent({
    message: 'composer.bridge.drift',
    level: 'warning',
    fingerprint: ['composer-bridge-drift', direction],
    tags: {
      direction,
      track: 'omni',
      fields: driftedFields.join(','),
      composerMode,
      sessionAgeBucket: bucketSessionAge(sessionAgeMs),
    },
    extra: {
      textLengthFlux: flux.text.length,
      textLengthZustand: zustand.text.length,
      sessionAgeMs,
    },
  })
  Sentry.addBreadcrumb({
    category: 'composer.bridge',
    level: 'warning',
    message: 'omni mirror drift',
    data: {
      direction,
      track: 'omni',
      fields: driftedFields,
      composerMode,
      sessionAgeMs,
    },
  })
}
